import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { LayoutProvider } from "./components/LayoutContext";
import LayoutComp from "./components/LayoutComp";
import { Provider } from "react-redux";
import store from "./redux-store/store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { clerkFrontendKey, clerkLiveFrontendKey } from "./utils/constants";
import * as Sentry from "@sentry/react";
import { Config } from "./config";
import { ClerkGate } from "./components/Wrappers/ClerkGate";

Sentry.init({
  dsn: "https://15f8778257b65a1a4685427345eabcdf@o4507612352675840.ingest.us.sentry.io/4507855232630784",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Set profilesSampleRate to 1.0 to profile every transaction.
  // Since profilesSampleRate is relative to tracesSampleRate,
  // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
  // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
  // results in 25% of transactions being profiled (0.5*0.5=0.25)
  profilesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const root = ReactDOM.createRoot(document.getElementById("root"));

/**
 * QueryClient Configuration - Optimized for role switching
 *
 * Key settings to prevent API storm during role changes:
 * - refetchOnWindowFocus: false - Prevents refetch when switching tabs/windows
 * - refetchOnReconnect: false - Prevents refetch on network reconnection
 * - retry: false - Prevents retry storms during role transitions
 * - staleTime: 5 minutes - Caches data to avoid unnecessary refetches
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes - prevents refetch storm during role switch
    },
  },
});

const clerkKey =
  process.env.REACT_APP_STAGE === "prod"
    ? clerkLiveFrontendKey
    : clerkFrontendKey;

root.render(
  <ClerkProvider
    publishableKey={clerkKey}
    allowedRedirectOrigins={[`${Config.apiGateway.HC_URL}`]}
  >
    <ClerkGate>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </Provider>
    </ClerkGate>
  </ClerkProvider>
);
